// Icon component — renders Font Awesome solid (filled) icons.
// Same `name` API as before so existing call sites don't change.
// FA solid icons are drawn as proper filled paths, not stroke-and-fill
// hybrids, so they look right at every size.
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faHouse, faMagnifyingGlass, faTrophy, faStar, faFire, faBolt, faGem,
  faWandMagicSparkles, faShield, faShieldHalved,
  faFileLines, faRightFromBracket, faRightToBracket, faTrash, faPenToSquare,
  faBell, faVolumeHigh, faVolumeXmark, faPlay, faPause, faForward, faBackward,
  faRotateLeft, faMaximize, faShareNodes, faDownload, faEnvelope, faMessage,
  faCommentDots, faLock, faLockOpen, faBullseye, faClock, faCheck, faXmark,
  faPlus, faMinus, faChevronLeft, faChevronRight, faChevronUp, faChevronDown,
  faArrowLeft, faArrowRight, faArrowUp, faArrowDown, faTriangleExclamation,
  faCircleExclamation, faCoins, faUser, faUsers, faGear, faEllipsisVertical,
  faEllipsis, faBars, faListUl, faSquareCheck, faPaperclip, faPaperPlane,
  faArrowUpRightFromSquare, faRobot, faChartSimple, faChartBar, faServer,
  faGlobe, faTerminal, faMicrochip, faDatabase, faCrown, faCalendar,
  faCalendarCheck, faNewspaper, faImage, faSpinner, faSliders, faSquare,
  faCircle, faMicrophone, faCopy,
  // Sticker variety
  faHeart, faMoon, faSun, faMusic, faLeaf, faPaw, faCloud, faRocket,
} from '@fortawesome/free-solid-svg-icons'

const icons = {
  // Navigation
  home: faHouse, search: faMagnifyingGlass,
  // Common
  trophy: faTrophy, sparkle: faWandMagicSparkles, sparkles: faWandMagicSparkles,
  flame: faFire, fire: faFire,
  zap: faBolt, gem: faGem,
  // Auth/account/settings
  shield: faShieldHalved, 'file-text': faFileLines,
  logout: faRightFromBracket, login: faRightToBracket,
  trash: faTrash, edit: faPenToSquare, pencil: faPenToSquare,
  bell: faBell, 'bell-dot': faBell,
  volume: faVolumeHigh, 'volume-off': faVolumeXmark,
  // Media
  play: faPlay, pause: faPause,
  'skip-forward': faForward, 'skip-backward': faBackward,
  'rotate-ccw': faRotateLeft, maximize: faMaximize, repeat: faRotateLeft,
  // Sharing / IO
  share: faShareNodes, download: faDownload,
  // Comms
  mail: faEnvelope, message: faMessage, 'message-circle': faCommentDots,
  // Locks / targets
  lock: faLock, 'lock-open': faLockOpen, target: faBullseye, crosshair: faBullseye,
  clock: faClock,
  // Status
  check: faCheck, close: faXmark, x: faXmark,
  plus: faPlus, minus: faMinus, 'add-box': faPlus,
  // Chevrons / arrows
  'chevron-left': faChevronLeft, 'chevron-right': faChevronRight,
  'chevron-up': faChevronUp, 'chevron-down': faChevronDown,
  'chevrons-up-down': faChevronDown,
  'arrow-left': faArrowLeft, 'arrow-right': faArrowRight,
  'arrow-up': faArrowUp, 'arrow-down': faArrowDown,
  // Alerts
  alert: faTriangleExclamation, 'alert-triangle': faTriangleExclamation,
  'alert-circle': faCircleExclamation,
  // Money / count
  coin: faCoins, coins: faCoins,
  // People
  user: faUser, users: faUsers, settings: faGear, gear: faGear,
  // Misc UI chrome
  'more-vertical': faEllipsisVertical, 'more-horizontal': faEllipsis,
  menu: faBars, list: faListUl, bulletlist: faListUl, 'list-box': faSquareCheck,
  'thumbs-up': faSquareCheck, 'thumbs-down': faSquareCheck,
  paperclip: faPaperclip, 'send-horizontal': faPaperPlane,
  'external-link': faArrowUpRightFromSquare,
  // Tech/dev (rarely used, keep mappings)
  robot: faRobot, chart: faChartSimple, 'chart-bar': faChartBar,
  server: faServer, globe: faGlobe, terminal: faTerminal, cpu: faMicrochip,
  database: faDatabase, crown: faCrown, sword: faShield,
  speed: faChartSimple, power: faBolt, wallet: faGem,
  // Calendars / news
  calendar: faCalendar, 'calendar-check': faCalendarCheck, newspaper: faNewspaper,
  // Misc
  image: faImage, loader: faSpinner, sliders: faSliders, 'sliders-2': faSliders,
  square: faSquare, circle: faCircle, mic: faMicrophone, copy: faCopy,
  folder: faFileLines, note: faFileLines,
  'align-left': faBars, 'align-center': faBars, 'align-right': faBars,
  star: faStar, shrimp: faStar, briefcase: faServer, clipboard: faFileLines, upload: faDownload,
  archive: faFileLines, 'layout-dashboard': faChartBar,
  moon: faMoon, sun: faSun, grid: faSquare, 'grid-3x3': faSquare,
  // Sticker icon variety
  heart: faHeart, music: faMusic, leaf: faLeaf, paw: faPaw,
  cloud: faCloud, rocket: faRocket,
}

const sizeClasses = {
  12: 'w-3 h-3', 14: 'w-3.5 h-3.5', 16: 'w-4 h-4',
  18: 'w-[18px] h-[18px]', 20: 'w-5 h-5', 22: 'w-[22px] h-[22px]',
  24: 'w-6 h-6', 28: 'w-7 h-7', 32: 'w-8 h-8', 40: 'w-10 h-10',
  48: 'w-12 h-12', 56: 'w-14 h-14', 64: 'w-16 h-16',
}

export default function Icon({ name, size = 24, className = '', style, color, ...rest }) {
  const def = icons[name] || icons.zap
  const sizeClass = sizeClasses[size] || `w-[${size}px] h-[${size}px]`
  // FontAwesomeIcon's SVG scales with font-size, not width/height, so the
  // Tailwind w-N h-N classes only set the bounding box. Apply the size as
  // fontSize too so the actual glyph scales to match.
  return (
    <FontAwesomeIcon
      icon={def}
      className={`${sizeClass} ${className}`}
      style={{ fontSize: size, ...(color ? { color } : {}), ...style }}
      aria-hidden="true"
      {...rest}
    />
  )
}
